import { useCallback, useContext } from "react";
import { useSubmitPartialApplicationMutation } from "src/apps/AppFlow/queries";
import { ApplicationFlowStateContext } from "src/apps/AppFlow/screens/root";
import { useModal } from "src/hooks";
import type { ModalContent } from "src/hooks/useModal";
import { ProblemIllustration } from "src/images";
import { ResponseError } from "src/lib";
import { Logger } from "src/log";
import { selectFullApplication } from "src/selectors";
import { selectApplicationFlowState } from "src/selectors/appFlowState";
import { clean } from "src/utilities/object";

import { ImmutableObjectError, RejectedReason, ValueError, enums, parsers, schemas } from "@fraction/shared";

export interface PartialSubmitOptions {
  onNext: () => void;
  validate?: () => boolean | Promise<boolean>;
  onInvalid?: (doSubmit: () => void) => void;
  disableNextIfInvalid?: boolean;
  on4XXError?: (err: ResponseError, proceed: () => void) => void;
  reasonsToBlock?: RejectedReason[] | "ANY";
}

const LOGGER = new Logger("pages.hooks.usePartialSubmit");
const STATUS_TO_ERROR_MODAL: Partial<Record<enums.ApplicationStatus, ModalContent>> = {
  [enums.ApplicationStatus.REJECTED]: {
    header: "Application locked",
    text:
      "This application was reviewed by Fraction's underwriting team and unfortunately it did not meet our " +
      "lending requirements. If you'd like to submit an additional property, please start a new application.",
    image: <ProblemIllustration width={188} height={167} />,
  },
};
export const GENERIC_ERROR_MODAL: ModalContent = {
  header: "Something went wrong",
  text: "Sorry, something went wrong. Please try again.",
  image: <ProblemIllustration width={188} height={167} />,
};
export const GENERIC_IMMUTABLE_ERROR_MODAL: ModalContent = {
  header: "Application locked",
  text:
    "Sorry, this application can no longer be updated. Please contact us for more information. If you'd " +
    "like to submit an application for a different property, please start a new application.",
  image: <ProblemIllustration width={188} height={167} />,
};
export const genRejectedModal = (messagesToUser: string[]): ModalContent => ({
  header: "Fraction Mortgage not available",
  text: [...messagesToUser, "You may go back and make changes to your application if necessary."],
  image: <ProblemIllustration width={188} height={167} />,
});

const defaultValidate = () => true;
const defaultOnInvalid = () => {};
const DEFAULT_REASONS_TO_BLOCK: RejectedReason[] = [];

/**
 * Helper hook for submitting a partial application within the application flow
 *
 * @param options.onNext Called when the partial submit is successful
 * @param options.validate Called to validate the partial submit. Returns true or false
 * @param options.onInvalid Called when `validate()` returns false. It is passed a `doSubmit` function which,
 *   if called, will proceed with the submission
 * @param options.disableNextIfInvalid If true, the next button will be disabled if `validate()` returns false
 * @param options.on4XXError Called when the partial submit mutation fails with a 400-level error. It is
 *   passed a `proceed` function which, if called, will proceed to the next page
 * @param options.reasonsToBlock We will only block the app flow if a rejected decision is one of these
 *   reasons. This allows each page to have total control over when it blocks progress. This also ensures that
 *   the user is only blocked when they still have the ability to make changes, if they want to
 *
 * @returns An object to help with submitting a partial application:
 *   - `handleNext`: Usually passed to the `onNext` prop of the `<FormFlowPage />` component
 *   - `nextDisabled`: Usually passed to the `nextDisabled` prop of the `<FormFlowPage />` component
 *   - `nextLoading`: Usually passed to the `nextLoading` prop of the `<FormFlowPage />` component
 */
const usePartialSubmit = ({
  onNext,
  validate = defaultValidate,
  onInvalid = defaultOnInvalid,
  disableNextIfInvalid = true,
  on4XXError = log4XXError,
  reasonsToBlock = DEFAULT_REASONS_TO_BLOCK,
}: PartialSubmitOptions) => {
  const { state, dispatch } = useContext(ApplicationFlowStateContext);
  const { showModal } = useModal();

  const shouldBlock = useCallback(
    (decision: schemas.DecisionSchema | undefined) => {
      if (decision?.decision === "rejected") {
        const reasons = (decision.reasons ?? []).map((r) => r.reason);
        return reasonsToBlock === "ANY" || reasons.some((r) => reasonsToBlock.includes(r));
      } else {
        return false;
      }
    },
    [reasonsToBlock]
  );
  const onSuccess = useCallback(
    (app: schemas.Application) => {
      const value = selectApplicationFlowState(app);
      if (dispatch) {
        dispatch({ type: "merge", value });
      } else {
        throw new ValueError(`dispatch() is unexpectedly ${dispatch} in usePartialSubmit()!`);
      }
      if (shouldBlock(app.rejectedDecision)) {
        const messagesToUser = (app.rejectedDecision?.reasons ?? []).map((r) => r.messageInFlow);
        showModal(genRejectedModal(messagesToUser));
      } else {
        onNext();
      }
    },
    [dispatch, showModal, onNext, shouldBlock]
  );
  const onError = useCallback(
    (err: ResponseError) => {
      if (err.name === ImmutableObjectError.NAME) {
        showModal(
          STATUS_TO_ERROR_MODAL[err.data?.status as enums.ApplicationStatus] ?? GENERIC_IMMUTABLE_ERROR_MODAL
        );
      } else if (err.statusCode >= 400 && err.statusCode < 500) {
        on4XXError(err, onNext);
      } else {
        LOGGER.exception(err, "Error during partial submission");
        showModal(GENERIC_ERROR_MODAL);
      }
    },
    [on4XXError, onNext, showModal]
  );

  const mutation = useSubmitPartialApplicationMutation({ onSuccess, onError });

  const doSubmit = useCallback(() => {
    const app = parsers.application.FULL_APPLICATION.parse(
      clean(selectFullApplication(clean(state), { defaults: false }))
    );
    mutation.mutate(app);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, mutation.mutate]);

  const handleNext = useCallback(
    async (event?: React.ChangeEvent<HTMLFormElement>) => {
      if (event?.preventDefault) {
        event.preventDefault();
      }

      if (validate()) {
        doSubmit();
      } else {
        onInvalid(doSubmit);
      }
    },
    [validate, onInvalid, doSubmit]
  );
  const submitHandler = useCallback(async () => handleNext(), [handleNext]);

  const nextDisabled = mutation.isPending || (disableNextIfInvalid && !validate());
  return { handleNext, submitHandler, nextDisabled, nextLoading: mutation.isPending };
};

const log4XXError: NonNullable<PartialSubmitOptions["on4XXError"]> = (err, proceed) => {
  // Since, currently, 400-level errors can be caused by data that comes from other pages, it would be
  // confusing to block or notify the user. So lets ignore and proceed to the next page, for now...
  LOGGER.warn("400-level error during partial submission; continuing...", err);
  proceed();
};

export default usePartialSubmit;
